import { createEventHook, type EventHookOn } from '@vueuse/core';
import { computed, onMounted, onScopeDispose, ref, watch, type Ref } from 'vue';
import { buildSearchCriteriaParams, type SearchCriteria } from '@/entities/searchCriteria';
import { searchLatest, type LowPriceDay } from '@/shared/api/searchLatest';
import type { SearchCriteriaParams } from '@/shared/model';
import type { LowPriceCalendarLoad } from './types';
import {
  getNextCalendarDateRange,
  getPreviousCalendarDateRange,
  resolveCalendarDateRange,
  LOW_PRICE_CALENDAR_DAYS,
} from '../lib/calendarDateRange';

const REQUEST_DELAY_MS = 300;

const MAX_DAYS = LOW_PRICE_CALENDAR_DAYS * 12;

export interface UseLowPriceCalendar {
  days: Ref<LowPriceDay[]>;
  isLoading: Ref<boolean>;
  isLoadingMore: Ref<boolean>;
  isAvailable: Ref<boolean>;
  loadMore: () => Promise<void>;
  loadPrevious: () => Promise<void>;
  onLoad: EventHookOn<LowPriceCalendarLoad>;
}

export function useLowPriceCalendar(criteria: Ref<SearchCriteria>): UseLowPriceCalendar {
  const days = ref<LowPriceDay[]>([]);
  const isLoading = ref(false);
  const isLoadingMore = ref(false);
  const isLoadingPrevious = ref(false);
  const hasMore = ref(true);
  const hasPrevious = ref(true);
  const calendarRange = ref<{ from: string; to: string } | null>(null);
  const loadedUntil = ref<string | null>(null);
  const loadedFrom = ref<string | null>(null);

  let pendingRequest: AbortController | null = null;
  let pendingPageRequest: AbortController | null = null;
  let pendingPreviousRequest: AbortController | null = null;
  let requestTimer: ReturnType<typeof setTimeout> | null = null;

  const loadHook = createEventHook<LowPriceCalendarLoad>();

  const requestDays = async (
    params: SearchCriteriaParams,
    signal: AbortSignal,
  ): Promise<LowPriceCalendarLoad | null> => {
    const searchCriteria = criteria.value;
    const startedAt = Date.now();
    const loaded = await searchLatest(params, signal);

    return (
      loaded && {
        criteria: searchCriteria,
        range: params.checkInDateRange,
        days: loaded,
        startedAt,
        finishedAt: Date.now(),
      }
    );
  };

  const isAvailable = computed<boolean>(() => (criteria.value.tourists?.rooms.length ?? 0) <= 1);

  watch(
    () => criteria.value.dateRange,
    (dateRange) => {
      calendarRange.value = resolveCalendarDateRange(dateRange, calendarRange.value);
    },
    { immediate: true },
  );

  const baseParams = computed<SearchCriteriaParams | null>(() => {
    if (!isAvailable.value) return null;

    const params = buildSearchCriteriaParams(criteria.value);

    if (!params.countries.length) return null;

    return params;
  });

  const requestParams = computed<SearchCriteriaParams | null>(() =>
    baseParams.value && calendarRange.value ? { ...baseParams.value, checkInDateRange: calendarRange.value } : null,
  );

  const requestKey = computed<string>(() => (requestParams.value ? JSON.stringify(requestParams.value) : ''));

  const load = async (params: SearchCriteriaParams): Promise<void> => {
    pendingRequest?.abort();
    pendingPageRequest?.abort();
    pendingPreviousRequest?.abort();

    const request = new AbortController();
    pendingRequest = request;

    const loaded = await requestDays(params, request.signal);

    if (request.signal.aborted) return;

    days.value = loaded?.days ?? [];
    loadedUntil.value = params.checkInDateRange.to;
    loadedFrom.value = params.checkInDateRange.from;
    hasMore.value = loaded === null || loaded.days.length > 0;
    isLoading.value = false;
    isLoadingMore.value = false;
    isLoadingPrevious.value = false;

    if (loaded) loadHook.trigger(loaded);
  };

  const scheduleLoad = (): void => {
    if (requestTimer) clearTimeout(requestTimer);

    pendingRequest?.abort();
    pendingPageRequest?.abort();
    pendingPreviousRequest?.abort();

    isLoading.value = true;
    hasMore.value = true;
    hasPrevious.value = true;

    requestTimer = setTimeout(() => {
      requestTimer = null;

      if (requestParams.value) load(requestParams.value);
    }, REQUEST_DELAY_MS);
  };

  const loadMore = async (): Promise<void> => {
    if (isLoading.value || isLoadingMore.value || !hasMore.value) return;
    if (!baseParams.value || !loadedUntil.value || days.value.length >= MAX_DAYS) return;

    const nextRange = getNextCalendarDateRange(loadedUntil.value);

    if (!nextRange) return;

    const request = new AbortController();
    pendingPageRequest = request;
    isLoadingMore.value = true;

    const loaded = await requestDays({ ...baseParams.value, checkInDateRange: nextRange }, request.signal);

    if (request.signal.aborted) return;

    if (loaded === null) {
      isLoadingMore.value = false;
      return;
    }

    days.value = [...days.value, ...loaded.days];
    loadedUntil.value = nextRange.to;
    hasMore.value = loaded.days.length > 0;
    isLoadingMore.value = false;

    loadHook.trigger(loaded);
  };

  const loadPrevious = async (): Promise<void> => {
    if (isLoading.value || isLoadingPrevious.value || !hasPrevious.value) return;
    if (!baseParams.value || !loadedFrom.value) return;

    const previousRange = getPreviousCalendarDateRange(loadedFrom.value);

    if (!previousRange) {
      hasPrevious.value = false;
      return;
    }

    const request = new AbortController();
    pendingPreviousRequest = request;
    isLoadingPrevious.value = true;

    const loaded = await requestDays({ ...baseParams.value, checkInDateRange: previousRange }, request.signal);

    if (request.signal.aborted) return;

    if (loaded === null) {
      isLoadingPrevious.value = false;
      return;
    }

    days.value = [...loaded.days, ...days.value];
    loadedFrom.value = previousRange.from;
    hasPrevious.value = loaded.days.length > 0;
    isLoadingPrevious.value = false;

    loadHook.trigger(loaded);
  };

  onMounted(() => {
    if (requestKey.value) scheduleLoad();
  });

  watch(requestKey, (key: string) => {
    if (key) {
      scheduleLoad();
      return;
    }

    pendingRequest?.abort();
    pendingPageRequest?.abort();
    pendingPreviousRequest?.abort();
    days.value = [];
    loadedUntil.value = null;
    loadedFrom.value = null;
    isLoading.value = false;
    isLoadingMore.value = false;
    isLoadingPrevious.value = false;
  });

  onScopeDispose(() => {
    if (requestTimer) clearTimeout(requestTimer);
    pendingRequest?.abort();
    pendingPageRequest?.abort();
    pendingPreviousRequest?.abort();
  });

  return { days, isLoading, isLoadingMore, isAvailable, loadMore, loadPrevious, onLoad: loadHook.on };
}
